/**
 * MedicationTab.jsx
 * + Hide/Show Medication List added
 */

import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Collapse,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";

import { useQuery, useMutation, gql } from "@apollo/client";

import MARMonthNavigator from "../mar/marMonthNavigator";
import MarCalendar from "../mar/marCalendar";
import MedPassModal from "../medPassModal";
import PrintableMar from "../../components/business/resident/printableMar";
import MarMonthlyView from "../../components/business/resident/marMonthlyView";

import { useReactToPrint } from "react-to-print";
import MedicationForm from "./medicationForm";
import MedicationList from "./medicationList";

/* ================== MUTATIONS ================== */

const SAVE_MEDICATION = gql`
  mutation SaveMedication($input: MedicationInput!) {
    saveMedication(input: $input) {
      id
    }
  }
`;

const DISCONTINUE_MEDICATION = gql`
  mutation DiscontinueMedication($id: ID!, $reason: String!, $replacementMedication: String) {
    discontinueMedication(id: $id, reason: $reason, replacementMedication: $replacementMedication)
  }
`;

const UPDATE_RESIDENT_CLINICAL_NOTES = gql`
  mutation UpdateResidentClinicalNotes($id: ID!, $data: ResidentInput!) {
    updateResident(id: $id, data: $data) {
      id
      businessId
      clinicalNotes
    }
  }
`;

/* ================== QUERIES ================== */

const GET_MEDICATIONS = gql`
  query GetMedications($residentId: ID!) {
    getMedications(residentId: $residentId) {
      id
      name
      dosage
      diagnosis
      sideEffects
      reason
      expiryDate
      schedule {
        frequency
        times
        daysOfWeek
        prn
      }
      startDate
      endDate
    }
  }
`;

const GET_MAR_MONTH = gql`
  query GetMarMonth($residentId: ID!, $month: Int!, $year: Int!) {
    getMarMonth(residentId: $residentId, month: $month, year: $year) {
      id
      medId
      medName
      dosage
      date
      timeOfDay
      status
      signature
    }
  }
`;
const GET_RESIDENT = gql`
  query GetResidentById($id: ID!) {
    getResidentById(id: $id) {
      id
      businessId
      firstName
      lastName
      clinicalNotes
    }
  }
`;

/* ================== COMPONENT ================== */

export default function MedicationTab({ residentId }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [addOpen, setAddOpen] = useState(false);
  const [editMedication, setEditMedication] = useState(null);
  const [selectedDose, setSelectedDose] = useState(null);

  /* NEW: hide/show toggler */
  const [showList, setShowList] = useState(false);
  const [showClinicalNotes, setShowClinicalNotes] = useState(false);
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [clinicalNotesDirty, setClinicalNotesDirty] = useState(false);
  const [editingClinicalNotes, setEditingClinicalNotes] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [medicationToDeactivate, setMedicationToDeactivate] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState("Dosage changed");
  const [replacementMedication, setReplacementMedication] = useState("");

  const month = selectedDate.getMonth() + 1;
  const year = selectedDate.getFullYear();

  const { data: medData, refetch: refetchMeds } = useQuery(
    GET_MEDICATIONS,
    { variables: { residentId } }
  );

  const { data: residentData } = useQuery(GET_RESIDENT, {
    variables: { id: residentId }
  });

  const { data: marData, refetch: refetchMar } = useQuery(
    GET_MAR_MONTH,
    { variables: { residentId, month, year } }
  );

  const [saveMedication] = useMutation(SAVE_MEDICATION);
  const [discontinueMedication, { loading: discontinuingMedication }] = useMutation(DISCONTINUE_MEDICATION);
  const [updateResidentClinicalNotes, { loading: savingClinicalNotes }] = useMutation(
    UPDATE_RESIDENT_CLINICAL_NOTES
  );

  const meds = medData?.getMedications || [];
  const doses = marData?.getMarMonth || [];
  const residentInfo = residentData?.getResidentById;

  const handleSaveMedication = async (payload) => {
    await saveMedication({ variables: { input: payload } });
    await refetchMeds();
    await refetchMar();
    setAddOpen(false);
    setEditMedication(null);
  };

  const handleSaveManyMedications = async (payloads) => {
    if (!Array.isArray(payloads) || payloads.length === 0) return;

    for (const payload of payloads) {
      await saveMedication({ variables: { input: payload } });
    }

    await refetchMeds();
    await refetchMar();
    setAddOpen(false);
    setEditMedication(null);
  };

  const handleDeleteMedication = async (medication) => {
    setMedicationToDeactivate(medication);
    setDeactivateReason("Dosage changed");
    setReplacementMedication("");
    setDeactivateOpen(true);
  };

  const handleConfirmDeactivateMedication = async () => {
    if (!medicationToDeactivate?.id || !deactivateReason) return;
    await discontinueMedication({
      variables: {
        id: medicationToDeactivate.id,
        reason: deactivateReason,
        replacementMedication: replacementMedication || null,
      },
    });
    await refetchMeds();
    await refetchMar();
    setDeactivateOpen(false);
    setMedicationToDeactivate(null);
  };

  useEffect(() => {
    const notesFromServer = residentData?.getResidentById?.clinicalNotes || "";
    setClinicalNotes(notesFromServer);
    setClinicalNotesDirty(false);
    setEditingClinicalNotes(false);
  }, [residentData?.getResidentById?.id, residentData?.getResidentById?.clinicalNotes]);

  const handleSaveClinicalNotes = async () => {
    const resident = residentData?.getResidentById;
    if (!resident?.id || !resident?.businessId) return;
    await updateResidentClinicalNotes({
      variables: {
        id: resident.id,
        data: {
          businessId: resident.businessId,
          firstName: resident.firstName || "",
          lastName: resident.lastName || "",
          clinicalNotes: clinicalNotes || "",
        },
      },
    });
    setClinicalNotesDirty(false);
    setEditingClinicalNotes(false);
  };

  // PRINT SETUP
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Monthly MAR",
    removeAfterPrint: false
  });


  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Medications & MAR
      </Typography>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          onClick={() => {
            setEditMedication(null);
            setAddOpen(true);
          }}
        >
          Add Medication
        </Button>

        {/* NEW: Toggle Button */}
        <Button
          variant="outlined"
          onClick={() => setShowList((prev) => !prev)}
        >
          {showList ? "Hide Medication List" : "Show Medication List"}
        </Button>
      </Stack>

      <Box
        sx={{
          mt: 2,
          mb: 2,
          p: 1.5,
          border: "1px solid #e2e8f0",
          borderRadius: 1.5,
          backgroundColor: "#f8fafc",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Diagnosis / Side Effects / Reasons
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowClinicalNotes((prev) => !prev)}
          >
            {showClinicalNotes ? "Collapse" : "Expand"}
          </Button>
        </Stack>
        <Collapse in={showClinicalNotes}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1.5 }}
            label="Diagnosis / Side Effects / Reasons"
            placeholder=""
            value={clinicalNotes}
            InputProps={{ readOnly: !editingClinicalNotes }}
            onChange={(e) => {
              if (!editingClinicalNotes) return;
              const next = e.target.value;
              setClinicalNotes(next);
              setClinicalNotesDirty(true);
            }}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={editingClinicalNotes ? handleSaveClinicalNotes : () => setEditingClinicalNotes(true)}
              disabled={savingClinicalNotes || (editingClinicalNotes && !clinicalNotesDirty)}
            >
              {savingClinicalNotes
                ? "Saving..."
                : editingClinicalNotes
                  ? "Save Notes"
                  : "Edit Notes"}
            </Button>
          </Stack>
        </Collapse>
      </Box>

      {/* FORM */}
      <MedicationForm
        open={addOpen}
        onClose={() => setAddOpen(false)}
        residentId={residentId}
        onSave={handleSaveMedication}
        onSaveMany={handleSaveManyMedications}
        editMedication={editMedication}
      />

      <Divider sx={{ my: 2 }} />

      {/* NEW WRAP IN COLLAPSE */}
      <Collapse in={showList}>
        <MedicationList
          meds={meds}
          onEdit={(m) => {
            setEditMedication(m);
            setAddOpen(true);
          }}
          onDelete={handleDeleteMedication}
        />

        <Divider sx={{ my: 3 }} />
      </Collapse>

      {/* MAR Navigator */}
      <MARMonthNavigator
        selectedDate={selectedDate}
        onChangeDate={setSelectedDate}
      />

      <Box
        sx={{
          mt: 2,
          mb: 1,
          p: 1.5,
          border: "1px solid #e2e8f0",
          borderRadius: 1.5,
          backgroundColor: "#f8fafc",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Daily MAR Actions
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Use the <strong>Update MAR Status</strong> button on each row to avoid accidental entries.
        </Typography>
      </Box>

      {/* Daily MAR */}
      <MarCalendar
        date={selectedDate}
        meds={meds}
        doses={doses}
        onLogDose={(dose) => setSelectedDose(dose)}
      />

      {/* Dose modal */}
      <MedPassModal
        open={Boolean(selectedDose)}
        dose={selectedDose}
        residentId={residentId}
        onClose={() => {
          setSelectedDose(null);
          refetchMar();
        }}
      />

      <Divider sx={{ my: 3 }} />

      {/* Monthly MAR */}
      <MarMonthlyView
        meds={meds}
        doses={doses}
        selectedDate={selectedDate}
        onCellClick={(dose) => setSelectedDose(dose)}
      />

      <Button variant="outlined" sx={{ mt: 2 }} onClick={handlePrint}>
        Print Monthly MAR
      </Button>

      <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deactivate Medication</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This medication will be set inactive and hidden from active lists.
          </Typography>
          <TextField
            fullWidth
            select
            label="Reason"
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
          >
            <MenuItem value="Dosage changed">Dosage changed</MenuItem>
            <MenuItem value="Medication expired">Medication expired</MenuItem>
            <MenuItem value="Medication completed">Medication completed</MenuItem>
            <MenuItem value="Provider discontinued">Provider discontinued</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Replacement Medication (Optional)"
            placeholder="e.g. Metoprolol 50mg"
            value={replacementMedication}
            onChange={(e) => setReplacementMedication(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmDeactivateMedication}
            disabled={discontinuingMedication}
          >
            {discontinuingMedication ? "Saving..." : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>


      <div style={{ position: "absolute", top: -9999, left: -9999 }}>
        <PrintableMar
          ref={printRef}
          meds={meds}
          doses={doses}
          selectedDate={selectedDate}
          resident={residentInfo}
        />
      </div>
    </Box>
  );
}
